import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import JobDetailsModal from "@/components/JobDetailsModal";
import AdvancedJobDetailsModalForCrew from "@/components/AdvancedJobDetailsModalForCrew";
import CancelCrewMemberModal from "@/components/CancelCrewMemberModal";
import { getAdminEmail } from "@/utils/adminAuth";
import {
  Info, Landmark, Copy, Mail, Star, CheckCircle2, AlertTriangle,
  XCircle, Check, Loader2, X
} from "lucide-react";
import { toast } from 'sonner';

const AdminPaymentRelease = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [stripeTransactions, setStripeTransactions] = useState([]);
  const [transactionsMessage, setTransactionsMessage] = useState('');
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);  // { job, crewMember, crewMemberIndex }
  const adminEmail = getAdminEmail();

  const openCancelModal = ({ job, crewMember, crewMemberIndex }) => {
    setCancelTarget({ job, crewMember, crewMemberIndex });
  };

  const closeCancelModal = () => setCancelTarget(null);

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');

    fetchReleasePaymentJobs();
  }, []);

  const fetchReleasePaymentJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/release-payment`
      );

      if (response.data.success) {
        setJobs(response.data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to fetch payment release jobs:', error);
      toast.error('Failed to load payment release jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewJobDetails = (job) => {
    setSelectedJob(job);
    setShowJobDetails(true);
  };

  const fetchStripeTransactions = async (job) => {
    setLoadingTransactions(true);
    setStripeTransactions([]);
    setTransactionsMessage('');
    try {
      const params = new URLSearchParams();
      // Per-crew release: filter charges to this crew only so the admin doesn't see
      // another hired crew's booking charge on the same job.
      if (job.is_per_crew_release) {
        if (job.invoice_id) params.set('invoice_id', job.invoice_id);
        if (job.crew_user_id) params.set('crew_user_id', job.crew_user_id);
        if (job.crew_role) params.set('crew_role', job.crew_role);
      }
      const qs = params.toString();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${job.id}/stripe-transactions${qs ? `?${qs}` : ''}`;
      const response = await axios.get(url);
      if (response.data.success) {
        setStripeTransactions(response.data.transactions || []);
        setTransactionsMessage(response.data.message || '');
      }
    } catch (error) {
      console.error('Failed to fetch stripe transactions:', error);
      setTransactionsMessage('Failed to load Stripe transactions');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleProcessPayment = (job) => {
    setProcessingPayment(job);
    setShowConfirmModal(true);
    fetchStripeTransactions(job);
  };

  const confirmPaymentProcessed = async () => {
    if (submitting) return;  // guard against double-clicks while the request is in flight
    setSubmitting(true);
    try {
      if (processingPayment.is_per_crew_release && processingPayment.invoice_id) {
        // Per-crew invoice processing
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${processingPayment.invoice_id}/payment-processed`
        );
      } else {
        // Standard job payment processing
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${processingPayment.id}/payment-processed`
        );
      }

      toast.success(`Payment processed for "${processingPayment.title}"${processingPayment.crew_name ? ` - ${processingPayment.crew_name}` : ''}`);
      // Auto-close once the process completes
      setShowConfirmModal(false);
      setProcessingPayment(null);
      fetchReleasePaymentJobs();
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Failed to process payment');
    } finally {
      setSubmitting(false);
    }
  };

  const requestBankDetails = async (job) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/request-bank-details`,
        {
          job_id: job.id,
          crew_user_id: job.crew_user_id
        }
      );
      toast.success(`Bank details request sent to ${job.crew_name || 'crew member'}`);
    } catch (error) {
      console.error('Failed to send bank details request:', error);
      toast.error('Failed to send request email');
    }
  };

  const copyToClipboard = (value, label) => {
    if (value == null || value === '') return;
    try {
      navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatJobId = (job) => {
    return job.job_number || `JOB-${job.id?.substring(0, 6).toUpperCase()}`;
  };

  const isAdvancedJob = (job) => {
    return (job.crew_members && job.crew_members.length > 1) ||
           (job.dates && job.dates.length > 1) ||
           (job.crew_count && job.crew_count > 1);
  };

  // ----- derived helpers used by the redesigned view -----
  const avatarColors = ['#572B7C', '#0F766E', '#2563EB', '#78287C'];
  const initials = (name) =>
    (name || 'Crew Member')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || 'CM';

  const jobPayout = (job) => {
    const base = parseFloat(job.base_payment || job.budget || 0);
    const extra = parseFloat(job.extra_payment_amount || 0);
    const total = base + extra;
    if (job.total_crew_payout != null) return parseFloat(job.total_crew_payout);
    return total - total * 0.02;
  };

  const totalDue = jobs.reduce((sum, j) => sum + (isAdvancedJob(j) ? 0 : jobPayout(j)), 0);

  // Copyable bank-detail field (green block)
  const BankField = ({ label, value, copyable = true }) => (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.3px] text-[#15803D]">{label}</div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-sm font-bold text-[#064E3B] sz-mono truncate">{value || 'Not provided'}</span>
        {copyable && value && (
          <button
            type="button"
            title="Copy"
            onClick={() => copyToClipboard(value, label)}
            className="w-[22px] h-[22px] rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(16,122,87,.12)', color: '#0F766E' }}
          >
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-full">
      {/* Sticky header */}
      <header className="sz-header flex items-center justify-between gap-[18px] flex-wrap">
        <div>
          <h1 className="sz-h1 m-0">Payment Release</h1>
          <div className="text-[12.5px] text-[#8A90A2] mt-[3px]">
            Manual crew payouts to reconcile · transfer in your bank, then confirm
          </div>
        </div>
        <span
          className="inline-flex items-center gap-[7px] text-[12.5px] font-semibold rounded-full px-[14px] py-2"
          style={{ fontFamily: 'Poppins', color: '#0F766E', background: '#CCFBF1' }}
        >
          {formatCurrency(totalDue)} to transfer · {jobs.length} job{jobs.length !== 1 ? 's' : ''}
        </span>
      </header>

      {/* Scroll body */}
      <main className="max-w-[1180px]" style={{ padding: '22px 30px 56px' }}>
        {/* Info banner explaining the manual flow */}
        <div
          className="flex items-start gap-[11px] rounded-[13px] mb-[18px]"
          style={{ padding: '13px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE' }}
        >
          <Info className="w-[18px] h-[18px] flex-shrink-0 mt-px" style={{ color: '#2563EB' }} />
          <div className="text-[12.5px] leading-[1.55]" style={{ color: '#1E40AF' }}>
            Shootzu holds the client's funds. To pay a crew member,{' '}
            <strong>transfer their payout from Shootzu's bank using the BSB/account below</strong>, then click{' '}
            <strong>Process Payment → Confirm</strong> to record it. The confirm button moves no money — it marks the
            job complete and emails the crew.
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-10 h-10 mx-auto animate-spin" style={{ color: '#0F766E' }} />
            <p className="mt-4 text-[#8A90A2] text-sm">Loading payment releases…</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="sz-card p-12 text-center sz-fade">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: '#DCFCE7' }}
            >
              <CheckCircle2 className="w-7 h-7" style={{ color: '#15803D' }} />
            </div>
            <p className="sz-h3 mb-1">No payments pending release</p>
            <p className="text-sm text-[#8A90A2]">All job payments have been processed.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-[18px]">
            {jobs.map((job) => {
              const advanced = isAdvancedJob(job);
              const crewMembers = job.crew_members || [];

              const base = parseFloat(job.base_payment || job.budget || 0);
              const extra = parseFloat(job.extra_payment_amount || 0);
              const total = base + extra;
              const fee = total * 0.02;
              const crewPay = total - fee;
              const hasBank = !!job.crew_banking?.has_details;
              const av = avatarColors[(job.id || '').charCodeAt(0) % avatarColors.length] || avatarColors[0];
              const rating = parseFloat(job.rating || 0);

              return (
                <div key={job.id} className="sz-card overflow-hidden sz-fade">
                  {/* Card header */}
                  <div
                    className="flex items-start justify-between gap-[14px] flex-wrap"
                    style={{ padding: '18px 22px', borderBottom: '1px solid #F1F3F7' }}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-[10.5px] font-bold rounded-full px-[10px] py-1"
                          style={{ fontFamily: 'Poppins', color: '#0F766E', background: '#CCFBF1' }}
                        >
                          PAYMENT RELEASE
                        </span>
                        {advanced && (
                          <span
                            className="text-[10.5px] font-bold rounded-full px-[10px] py-1"
                            style={{ fontFamily: 'Poppins', color: '#78287C', background: '#EDE4F4' }}
                          >
                            ADVANCED
                          </span>
                        )}
                        {job.is_per_crew_release && (
                          <span
                            className="text-[10.5px] font-bold rounded-full px-[10px] py-1"
                            style={{ fontFamily: 'Poppins', color: '#4338CA', background: '#E0E7FF' }}
                          >
                            PER-CREW RELEASE
                          </span>
                        )}
                        <span className="text-[11px] text-[#A8AEBC] sz-mono">{formatJobId(job)}</span>
                      </div>
                      <h3 className="sz-h3 mt-[11px] text-[17px]">
                        {job.title}
                        {job.is_per_crew_release && job.crew_role && (
                          <span className="text-sm font-normal text-[#8A90A2] ml-2">
                            ({job.crew_role.replace(/_/g, ' ')})
                          </span>
                        )}
                      </h3>
                      <div className="text-[12.5px] text-[#8A90A2] mt-1">
                        {formatDate(job.date)}
                        {' · '}
                        {advanced ? `${crewMembers.length} crew` : `${job.hours || 0}h`}
                        {(job.location || job.location_address) ? ` · ${job.location || job.location_address}` : ''}
                      </div>
                    </div>
                    <div className="text-right flex items-start gap-3">
                      <div>
                        <div className="text-[11px] text-[#8A90A2]">Crew payout (98%)</div>
                        <div
                          className="text-[22px] font-bold tracking-[-0.4px]"
                          style={{ fontFamily: 'Poppins', color: '#15803D' }}
                        >
                          {advanced
                            ? formatCurrency(crewMembers.reduce((s, m) => s + (m.subtotal || (m.hours * m.ratePerHour) || 0), 0))
                            : formatCurrency(jobPayout(job))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleViewJobDetails(job)}
                        className="sz-btn sz-btn-outline"
                      >
                        View Details
                      </button>
                    </div>
                  </div>

                  {/* Advanced jobs: per-crew rows, single column */}
                  {advanced ? (
                    <div className="p-[22px]">
                      <div
                        className="text-[11px] font-bold uppercase tracking-[0.3px] mb-3"
                        style={{ fontFamily: 'Poppins', color: '#2563EB' }}
                      >
                        Crew Payment Details
                      </div>
                      <div className="flex flex-col gap-4">
                        {crewMembers.map((member, idx) => {
                          const mHasBank = !!(member.bank_bsb && member.bank_account);
                          return (
                            <div key={idx} className="sz-card p-4">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-[11px] mb-3">
                                    <div
                                      className="w-10 h-10 rounded-full text-white flex items-center justify-center text-[13px] font-bold flex-shrink-0"
                                      style={{ fontFamily: 'Poppins', background: avatarColors[idx % avatarColors.length] }}
                                    >
                                      {initials(member.crew_name || `Crew Member ${idx + 1}`)}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="text-[13.5px] font-semibold truncate">
                                        {member.crew_name || `Crew Member ${idx + 1}`}
                                      </div>
                                      <div className="text-[11.5px] text-[#8A90A2]">
                                        {member.role?.replace(/_/g, ' ')} · {member.hours}h @ ${member.ratePerHour}/hr
                                      </div>
                                    </div>
                                  </div>

                                  {/* Bank details block */}
                                  <div
                                    className="rounded-[13px]"
                                    style={{
                                      padding: '14px 15px',
                                      border: `1.5px solid ${mHasBank ? '#BBF7D0' : '#FECACA'}`,
                                      background: mHasBank ? '#F0FDF4' : '#FEF2F2',
                                    }}
                                  >
                                    <div className="flex items-center gap-[7px] mb-3">
                                      <Landmark
                                        className="w-[15px] h-[15px]"
                                        style={{ color: mHasBank ? '#15803D' : '#B91C1C' }}
                                      />
                                      <span
                                        className="text-[11.5px] font-bold uppercase tracking-[0.3px]"
                                        style={{ fontFamily: 'Poppins', color: mHasBank ? '#15803D' : '#B91C1C' }}
                                      >
                                        {mHasBank ? 'Bank details — pay to this account' : 'Bank details missing'}
                                      </span>
                                    </div>
                                    {mHasBank ? (
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-[11px]">
                                        <BankField label="Account name" value={member.bank_account_name || member.crew_name} copyable={false} />
                                        <BankField label="BSB" value={member.bank_bsb} />
                                        <BankField label="Account no." value={member.bank_account} />
                                      </div>
                                    ) : (
                                      <div className="text-[12.5px]" style={{ color: '#991B1B' }}>
                                        This crew member has not added bank details. You can't transfer their payout yet.
                                      </div>
                                    )}
                                  </div>

                                  <div className="mt-3 text-sm font-bold text-[#0C1120]">
                                    Payment Due:{' '}
                                    <span style={{ color: '#15803D' }}>
                                      {formatCurrency(member.subtotal || (member.hours * member.ratePerHour))}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex flex-col gap-2 sm:w-auto">
                                  <button
                                    type="button"
                                    onClick={() => handleProcessPayment({ ...job, crew_member: member, position: idx + 1 })}
                                    className="sz-btn"
                                    style={{ background: '#15803D', color: '#fff' }}
                                  >
                                    <Check className="w-4 h-4" />
                                    Process Payment
                                  </button>
                                  {member.hired_user_id && (
                                    <button
                                      type="button"
                                      onClick={() => openCancelModal({
                                        job,
                                        crewMemberIndex: idx,
                                        crewMember: {
                                          userId: member.hired_user_id,
                                          name: member.hired_name || member.crew_name,
                                          email: member.hired_email || member.crew_email,
                                          role: member.role,
                                          subtotal: member.subtotal || ((member.hours || 0) * (member.ratePerHour || 0)),
                                          photo: member.hired_photo
                                        }
                                      })}
                                      className="sz-btn"
                                      style={{ border: '1px solid #FECACA', background: '#fff', color: '#B91C1C' }}
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Cancel (No-Show)
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* Simple job: two columns */
                    <div className="grid grid-cols-1 md:grid-cols-[1.25fr_1fr]">
                      {/* Left: Crew Payment Details */}
                      <div className="md:border-r" style={{ padding: '18px 22px', borderColor: '#F1F3F7' }}>
                        <div
                          className="text-[11px] font-bold uppercase tracking-[0.3px] mb-3"
                          style={{ fontFamily: 'Poppins', color: '#2563EB' }}
                        >
                          Crew Payment Details
                        </div>

                        <div className="flex items-center gap-[11px] mb-[13px]">
                          <div
                            className="w-10 h-10 rounded-full text-white flex items-center justify-center text-[13px] font-bold flex-shrink-0"
                            style={{ fontFamily: 'Poppins', background: av }}
                          >
                            {initials(job.crew_name)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-[13.5px] font-semibold truncate">{job.crew_name || 'Crew Member'}</div>
                            <div className="text-[11.5px] text-[#8A90A2]">
                              {(job.crew_role ? job.crew_role.replace(/_/g, ' ') + ' · ' : '')}
                              {job.hours || 0}h @ ${job.pay_rate || 0}/hr
                            </div>
                          </div>
                        </div>

                        {/* Breakdown */}
                        <div className="rounded-[11px]" style={{ background: '#F7F8FB', padding: '13px 15px' }}>
                          <div className="flex items-center justify-between py-[5px]" style={{ borderBottom: '1px solid #E6E9EF' }}>
                            <span className="text-[12.5px] text-[#2B3142] font-medium">Base payment</span>
                            <span className="text-[12.5px] font-medium text-[#0C1120]" style={{ fontFamily: 'Poppins' }}>{formatCurrency(base)}</span>
                          </div>
                          <div className="flex items-center justify-between py-[5px]" style={{ borderBottom: '1px solid #E6E9EF' }}>
                            <span className="text-[12.5px] text-[#2B3142] font-medium">
                              Additional{extra > 0 && job.extra_payment_reason ? ` (${job.extra_payment_reason})` : ''}
                            </span>
                            <span className="text-[12.5px] font-medium text-[#0C1120]" style={{ fontFamily: 'Poppins' }}>{formatCurrency(extra)}</span>
                          </div>
                          <div className="flex items-center justify-between py-[5px]" style={{ borderBottom: '1px solid #E6E9EF' }}>
                            <span className="text-[12.5px] text-[#2B3142] font-semibold">Job total</span>
                            <span className="text-[12.5px] font-semibold text-[#0C1120]" style={{ fontFamily: 'Poppins' }}>{formatCurrency(total)}</span>
                          </div>
                          <div className="flex items-center justify-between py-[5px]" style={{ borderBottom: '1px solid #E6E9EF' }}>
                            <span className="text-[12.5px] font-medium" style={{ color: '#B91C1C' }}>Platform fee (2%)</span>
                            <span className="text-[12.5px] font-medium" style={{ fontFamily: 'Poppins', color: '#B91C1C' }}>−{formatCurrency(fee)}</span>
                          </div>
                          <div className="flex items-center justify-between py-[5px]">
                            <span className="text-[12.5px] font-bold text-[#0C1120]">Crew payment (98%)</span>
                            <span className="text-[15px] font-bold" style={{ fontFamily: 'Poppins', color: '#15803D' }}>{formatCurrency(crewPay)}</span>
                          </div>
                        </div>

                        {/* Bank details — operational payload */}
                        <div
                          className="mt-[14px] rounded-[13px]"
                          style={{
                            padding: '14px 15px',
                            border: `1.5px solid ${hasBank ? '#BBF7D0' : '#FECACA'}`,
                            background: hasBank ? '#F0FDF4' : '#FEF2F2',
                          }}
                        >
                          <div className="flex items-center gap-[7px]" style={{ marginBottom: hasBank ? 12 : 8 }}>
                            {hasBank ? (
                              <Landmark className="w-[15px] h-[15px]" style={{ color: '#15803D' }} />
                            ) : (
                              <AlertTriangle className="w-[15px] h-[15px]" style={{ color: '#B91C1C' }} />
                            )}
                            <span
                              className="text-[11.5px] font-bold uppercase tracking-[0.3px]"
                              style={{ fontFamily: 'Poppins', color: hasBank ? '#15803D' : '#B91C1C' }}
                            >
                              {hasBank ? 'Bank details — pay to this account' : 'Bank details missing'}
                            </span>
                          </div>
                          {hasBank ? (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-[11px]">
                              <BankField label="Account name" value={job.crew_banking?.account_name} copyable={false} />
                              <BankField label="Bank" value={job.crew_banking?.bank_name} copyable={false} />
                              <BankField label="BSB" value={job.crew_banking?.bsb} />
                              <BankField label="Account no." value={job.crew_banking?.account_number} />
                            </div>
                          ) : (
                            <>
                              <div className="text-[12.5px] leading-[1.5] mb-[11px]" style={{ color: '#991B1B' }}>
                                This crew member has not added bank details. You can't transfer their payout yet —
                                request them below.
                              </div>
                              <button
                                type="button"
                                onClick={() => requestBankDetails(job)}
                                className="sz-btn sz-btn-danger"
                              >
                                <Mail className="w-[14px] h-[14px]" />
                                Ask for Bank Details
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Right: funds verification + review + CTA */}
                      <div className="flex flex-col" style={{ padding: '18px 22px' }}>
                        {/* Inbound funds verification (distinct from payout) */}
                        <div
                          className="flex items-center gap-[9px] rounded-[11px] mb-[14px]"
                          style={{
                            padding: '11px 13px',
                            background: '#F0FDF4',
                            border: '1px solid #BBF7D0',
                          }}
                        >
                          <CheckCircle2 className="w-[17px] h-[17px] flex-shrink-0" style={{ color: '#15803D' }} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-bold" style={{ color: '#15803D' }}>
                              Funds collected from client
                            </div>
                            <div className="text-[11px] text-[#8A90A2]">
                              Held by Shootzu · {formatCurrency(total)}
                            </div>
                          </div>
                        </div>

                        <div
                          className="text-[11px] font-bold uppercase tracking-[0.3px] mb-2"
                          style={{ fontFamily: 'Poppins', color: '#B45309' }}
                        >
                          Client Review
                        </div>
                        <div className="flex gap-[3px] items-center mb-2">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star
                              key={s}
                              className="w-4 h-4"
                              style={{
                                fill: s <= Math.round(rating) ? '#F59E0B' : '#E6E9EF',
                                color: s <= Math.round(rating) ? '#F59E0B' : '#E6E9EF',
                              }}
                            />
                          ))}
                          <span className="text-[13px] font-bold ml-[5px]" style={{ fontFamily: 'Poppins' }}>
                            {(job.rating || 0)}
                          </span>
                        </div>
                        <p className="text-[12.5px] leading-[1.55] m-0 flex-1" style={{ color: '#2B3142' }}>
                          {job.review_comment ? `“${job.review_comment}”` : <span className="text-[#A8AEBC]">No review left.</span>}
                        </p>

                        {/* CTA + cancel */}
                        {job.is_per_crew_release && job.hired_user_id && (
                          <button
                            type="button"
                            onClick={() => openCancelModal({
                              job,
                              crewMemberIndex: job.crew_member_index,
                              crewMember: {
                                userId: job.hired_user_id,
                                name: job.crew_name,
                                email: job.crew_email,
                                role: job.crew_role,
                                subtotal: job.subtotal || job.base_payment || 0,
                                photo: job.hired_photo
                              }
                            })}
                            className="sz-btn mt-[10px] w-full"
                            style={{ border: '1px solid #FECACA', background: '#fff', color: '#B91C1C' }}
                          >
                            <XCircle className="w-4 h-4" />
                            Cancel (No-Show)
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleProcessPayment(job)}
                          disabled={!hasBank}
                          className="flex items-center justify-center gap-2 w-full mt-[14px] rounded-[11px] text-[13px] font-semibold"
                          style={{
                            padding: '12px',
                            background: hasBank ? '#15803D' : '#FEE2E2',
                            color: hasBank ? '#fff' : '#B91C1C',
                            cursor: hasBank ? 'pointer' : 'not-allowed',
                          }}
                        >
                          {hasBank ? 'Process Payment' : 'Awaiting bank details'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Cancel Crew Position (No-Show) Modal */}
      {cancelTarget && (
        <CancelCrewMemberModal
          job={cancelTarget.job}
          crewMember={cancelTarget.crewMember}
          crewMemberIndex={cancelTarget.crewMemberIndex}
          userEmail={adminEmail}
          isOpen={!!cancelTarget}
          onClose={closeCancelModal}
          onSuccess={() => {
            closeCancelModal();
            fetchReleasePaymentJobs();
          }}
          adminMode
        />
      )}

      {/* Job Details Modal */}
      {showJobDetails && selectedJob && (
        isAdvancedJob(selectedJob) ? (
          <AdvancedJobDetailsModalForCrew
            job={selectedJob}
            isOpen={showJobDetails}
            onClose={() => {
              setShowJobDetails(false);
              setSelectedJob(null);
            }}
          />
        ) : (
          <JobDetailsModal
            job={selectedJob}
            isOpen={showJobDetails}
            onClose={() => {
              setShowJobDetails(false);
              setSelectedJob(null);
            }}
            showApplyButton={false}
          />
        )
      )}

      {/* Process Crew Payment (reconciliation) Modal */}
      {showConfirmModal && processingPayment && (() => {
        const basePayment = parseFloat(processingPayment.base_payment || processingPayment.budget || 0);
        const extraPayment = parseFloat(processingPayment.extra_payment_amount || 0);
        const jobTotal = basePayment + extraPayment;
        const platformFee = jobTotal * 0.02;
        const crewPayment = jobTotal * 0.98;
        const hasBank = !!processingPayment.crew_banking?.has_details;
        const totalCharged = stripeTransactions.reduce((sum, t) => sum + (t.stripe_amount || 0), 0);
        const charged = !loadingTransactions && stripeTransactions.length > 0;

        return (
          <div className="sz-modal-backdrop z-[100]" onClick={() => !submitting && setShowConfirmModal(false)}>
            <div
              className="sz-modal-card w-full max-w-[520px] relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Emerald header */}
              <div
                className="flex items-center justify-between rounded-t-[19px]"
                style={{ padding: '18px 24px', background: 'linear-gradient(120deg,#0F766E,#15803D)', color: '#fff' }}
              >
                <div>
                  <h3 className="text-[17px] font-bold m-0" style={{ fontFamily: 'Poppins' }}>Process Crew Payment</h3>
                  <div className="text-[11.5px] mt-0.5" style={{ color: 'rgba(255,255,255,.82)' }}>
                    Reconcile a manual bank transfer
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => !submitting && setShowConfirmModal(false)}
                  className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,.18)', color: '#fff' }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="szscroll flex-1 overflow-y-auto" style={{ padding: '18px 24px' }}>
                {/* Job + crew */}
                <div className="grid grid-cols-2 gap-[14px] mb-4">
                  <div className="rounded-[12px]" style={{ background: '#F7F8FB', padding: '12px 14px' }}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.3px] text-[#8A90A2] mb-[5px]" style={{ fontFamily: 'Poppins' }}>Job</div>
                    <div className="text-[13px] font-semibold truncate">{processingPayment.title}</div>
                    <div className="text-[11.5px] text-[#8A90A2] mt-0.5">{formatDate(processingPayment.date)} · {formatJobId(processingPayment)}</div>
                  </div>
                  <div className="rounded-[12px]" style={{ background: '#F7F8FB', padding: '12px 14px' }}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.3px] text-[#8A90A2] mb-[5px]" style={{ fontFamily: 'Poppins' }}>Crew member</div>
                    <div className="text-[13px] font-semibold truncate">
                      {processingPayment.crew_name || processingPayment.assigned_crew_name || 'N/A'}
                    </div>
                    <div className="text-[11.5px] text-[#8A90A2] mt-0.5 truncate">{processingPayment.crew_email || 'N/A'}</div>
                  </div>
                </div>

                {/* Payment breakdown */}
                <div className="text-[11px] font-bold uppercase tracking-[0.3px] text-[#8A90A2] mb-2" style={{ fontFamily: 'Poppins' }}>
                  Payment breakdown
                </div>
                <div className="rounded-[12px] mb-4" style={{ background: '#F7F8FB', padding: '13px 15px' }}>
                  <div className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #E6E9EF' }}>
                    <span className="text-[12.5px] text-[#2B3142] font-medium">Base payment</span>
                    <span className="text-[12.5px] font-medium text-[#0C1120]" style={{ fontFamily: 'Poppins' }}>{formatCurrency(basePayment)}</span>
                  </div>
                  {extraPayment > 0 && (
                    <div className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #E6E9EF' }}>
                      <span className="text-[12.5px] text-[#2B3142] font-medium">
                        Additional{processingPayment.extra_payment_reason ? ` (${processingPayment.extra_payment_reason})` : ''}
                      </span>
                      <span className="text-[12.5px] font-medium text-[#0C1120]" style={{ fontFamily: 'Poppins' }}>+{formatCurrency(extraPayment)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #E6E9EF' }}>
                    <span className="text-[12.5px] text-[#2B3142] font-semibold">Job total</span>
                    <span className="text-[12.5px] font-semibold text-[#0C1120]" style={{ fontFamily: 'Poppins' }}>{formatCurrency(jobTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #E6E9EF' }}>
                    <span className="text-[12.5px] font-medium" style={{ color: '#B91C1C' }}>Platform fee (2%)</span>
                    <span className="text-[12.5px] font-medium" style={{ fontFamily: 'Poppins', color: '#B91C1C' }}>−{formatCurrency(platformFee)}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-[12.5px] font-bold text-[#0C1120]">Crew payment (98%)</span>
                    <span className="text-[15px] font-bold" style={{ fontFamily: 'Poppins', color: '#15803D' }}>{formatCurrency(crewPayment)}</span>
                  </div>
                </div>

                {/* Funds collected from client (inbound verification) */}
                <div className="text-[11px] font-bold uppercase tracking-[0.3px] text-[#8A90A2] mb-2" style={{ fontFamily: 'Poppins' }}>
                  Funds collected from client
                </div>
                {loadingTransactions ? (
                  <div className="flex items-center justify-center gap-2 py-4 mb-4 rounded-[12px]" style={{ background: '#F7F8FB' }}>
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#0F766E' }} />
                    <span className="text-sm" style={{ color: '#0F766E' }}>Verifying with Stripe…</span>
                  </div>
                ) : charged ? (
                  <div className="rounded-[12px] mb-4" style={{ border: '1px solid #BBF7D0', background: '#F0FDF4', padding: '13px 15px' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" style={{ color: '#15803D' }} />
                        <span className="text-[12.5px] font-semibold" style={{ color: '#15803D' }}>Stripe charge verified</span>
                      </div>
                      <span className="text-[13px] font-bold" style={{ fontFamily: 'Poppins' }}>{formatCurrency(totalCharged)}</span>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {stripeTransactions.map((txn, idx) => (
                        <div key={idx} className="flex items-center justify-between text-[11px] text-[#8A90A2]">
                          <span className="sz-mono truncate mr-2">{txn.id}</span>
                          <span className="flex-shrink-0">
                            {txn.stripe_amount != null ? formatCurrency(txn.stripe_amount) : 'N/A'}
                            {txn.stripe_created ? ` · ${formatDate(txn.stripe_created)}` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-[12px] mb-4 flex gap-[9px]" style={{ border: '1px solid #FECACA', background: '#FEF2F2', padding: '13px 15px' }}>
                    <AlertTriangle className="w-[17px] h-[17px] flex-shrink-0 mt-px" style={{ color: '#B91C1C' }} />
                    <div className="text-[12px] leading-[1.5]" style={{ color: '#991B1B' }}>
                      <strong>Financial record incomplete — no charge IDs.</strong>{' '}
                      {transactionsMessage === 'No invoice found'
                        ? 'No invoice exists in the database for this job. Payment cannot be released until the invoice/Stripe linkage is repaired.'
                        : transactionsMessage === 'No charge IDs on invoice'
                        ? 'Invoice exists but has no Stripe charge IDs recorded. Payment cannot be released until the linkage is repaired.'
                        : (transactionsMessage || 'We can\'t verify the client was charged. Payment cannot be released.')}
                    </div>
                  </div>
                )}

                {/* Banking details — transfer to this account */}
                <div className="text-[11px] font-bold uppercase tracking-[0.3px] text-[#8A90A2] mb-2" style={{ fontFamily: 'Poppins' }}>
                  Banking details — transfer to this account
                </div>
                {hasBank ? (
                  <div className="rounded-[12px] mb-4" style={{ border: '1.5px solid #BBF7D0', background: '#F0FDF4', padding: '13px 15px' }}>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-[11px]">
                      <BankField label="Account name" value={processingPayment.crew_banking.account_name} copyable={false} />
                      <BankField label="Bank" value={processingPayment.crew_banking.bank_name} copyable={false} />
                      <BankField label="BSB" value={processingPayment.crew_banking.bsb} />
                      <BankField label="Account no." value={processingPayment.crew_banking.account_number} />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-[12px] mb-4" style={{ border: '1px solid #FECACA', background: '#FEF2F2', padding: '13px 15px' }}>
                    <div className="text-[12px] leading-[1.5] mb-[10px]" style={{ color: '#991B1B' }}>
                      <strong>No bank details on file.</strong> Request them before you can transfer and confirm.
                    </div>
                    <button
                      type="button"
                      onClick={() => requestBankDetails(processingPayment)}
                      className="sz-btn sz-btn-danger"
                    >
                      <Mail className="w-[14px] h-[14px]" />
                      Ask for Bank Details
                    </button>
                  </div>
                )}

                {/* Amber finalization note */}
                <div className="flex items-start gap-[11px] rounded-[12px]" style={{ padding: '13px 15px', background: '#FEF3C7', border: '1px solid #FDE68A' }}>
                  <AlertTriangle className="w-[18px] h-[18px] flex-shrink-0 mt-px" style={{ color: '#B45309' }} />
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-bold mb-[3px]" style={{ color: '#92400E' }}>
                      This confirms a transfer you've already made — it doesn't move money.
                    </div>
                    <div className="text-[11.5px] leading-[1.5]" style={{ color: '#A16207' }}>
                      It marks the job <strong>COMPLETED</strong>, generates the final invoice and emails the crew
                      “payment released”. Only confirm after you've transferred the funds in your bank.
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-[9px]" style={{ padding: '14px 24px', borderTop: '1px solid #EEF0F4' }}>
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={submitting}
                  className="sz-btn sz-btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmPaymentProcessed}
                  disabled={!hasBank || submitting}
                  className="flex items-center justify-center gap-[7px] rounded-[11px] text-[12.5px] font-semibold"
                  style={{
                    flex: 1.7,
                    padding: '12px',
                    background: hasBank ? '#15803D' : '#9CA3AF',
                    color: '#fff',
                    cursor: hasBank && !submitting ? 'pointer' : 'not-allowed',
                    opacity: hasBank && !submitting ? 1 : 0.7,
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-[15px] h-[15px] animate-spin" />
                      Processing…
                    </>
                  ) : (
                    <>
                      <Check className="w-[15px] h-[15px]" />
                      {hasBank ? `Confirm — I've transferred ${formatCurrency(crewPayment)}` : 'Awaiting bank details'}
                    </>
                  )}
                </button>
              </div>

              {/* Processing overlay */}
              {submitting && (
                <div className="absolute inset-0 rounded-[19px] flex flex-col items-center justify-center gap-3 z-10" style={{ background: 'rgba(255,255,255,.75)', backdropFilter: 'blur(2px)' }}>
                  <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#15803D' }} />
                  <p className="text-sm font-semibold text-[#0C1120]">Processing payment…</p>
                  <p className="text-xs text-[#8A90A2]">Please wait, this may take a few seconds</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AdminPaymentRelease;
